/* =========================================================
   VIVID — Ingredient cards (sections/vh-ingredient-cards.liquid)
   One card is open at a time. A click, Enter or Space on a closed card opens it; the
   arrow keys, Home and End move between the cards. On phones a tap on the open row
   closes it. Only the open card's video loads and plays, and only while the section
   is on screen (never with reduced motion). In the theme editor, selecting a card
   block opens it. The CSS does all the motion; this file only moves state:
   .is-open on the card, aria-expanded on its button, inert on the closed panels.
   ========================================================= */
(() => {
  const SELECTOR = '[data-section="vh-ingredient-cards"]';
  const phone = window.matchMedia('(max-width: 749px)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SETTLE_MS = 650;   // the phone row's height change (.6s in the CSS) plus a frame or two

  const init = (root) => {
    if (root._vhCards) return;
    const cards = [...root.querySelectorAll('.vh-cards__card')];
    if (!cards.length) return;
    const parts = cards.map((card) => ({
      card,
      toggle: card.querySelector('.vh-cards__toggle'),
      panel: card.querySelector('.vh-cards__panel'),
      video: card.querySelector('video')
    }));

    let open = cards.findIndex((c) => c.classList.contains('is-open'));
    let last = Math.max(open, 0);   // what a computer reopens if a phone closed everything
    let inView = false;
    let settle = 0;

    /* Videos: the open one plays while the section is on screen; the rest stay paused,
       and a video that has never been opened never downloads (preload="none"). */
    const syncVideos = () => {
      parts.forEach(({ video }, i) => {
        if (!video) return;
        if (i === open && inView && !reduce.matches) {
          if (video.preload !== 'auto') video.preload = 'auto';
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    };

    /* Phones: section-by-section snapping is held while a row changes height (the page
       would otherwise be snapped around mid-animation). Once it settles, a row that starts
       above the screen (the row that closed above it pulled it up) is brought back into
       view, and snapping resumes. */
    const html = document.documentElement;
    const settleInView = (card) => {
      clearTimeout(settle);
      html.classList.add('vh-snap-hold');
      settle = setTimeout(() => {
        const top = card ? card.getBoundingClientRect().top : 0;
        const margin = card ? parseFloat(getComputedStyle(card).scrollMarginTop) || 0 : 0;
        if (card && top < margin - 1) card.scrollIntoView({ block: 'start', behavior: 'auto' });
        html.classList.remove('vh-snap-hold');
      }, reduce.matches ? 0 : SETTLE_MS);
    };

    const setOpen = (index) => {
      if (index === open) return;
      const before = open;
      open = index;
      if (index >= 0) last = index;
      parts.forEach(({ card, toggle, panel }, i) => {
        const on = i === index;
        card.classList.toggle('is-open', on);
        if (toggle) {
          toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
          // Computers always keep one card open, so the open one can't be collapsed (APG accordion)
          if (on && !phone.matches) toggle.setAttribute('aria-disabled', 'true');
          else toggle.removeAttribute('aria-disabled');
        }
        if (panel) panel.inert = !on;
      });
      syncVideos();
      if (phone.matches) settleInView(index >= 0 && before >= 0 && before < index ? cards[index] : null);
    };

    const onClick = (e) => {
      const toggle = e.target.closest('.vh-cards__toggle');
      if (!toggle || !root.contains(toggle)) return;
      const i = parts.findIndex((p) => p.toggle === toggle);
      if (i < 0) return;
      if (i === open) {
        if (phone.matches) setOpen(-1);   // phones may close the open row; computers keep one open
        return;
      }
      setOpen(i);
    };

    const onKey = (e) => {
      const toggle = e.target.closest('.vh-cards__toggle');
      if (!toggle) return;
      const i = parts.findIndex((p) => p.toggle === toggle);
      if (i < 0) return;
      const n = parts.length;
      let next = -1;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowDown': next = (i + 1) % n; break;
        case 'ArrowLeft': case 'ArrowUp': next = (i - 1 + n) % n; break;
        case 'Home': next = 0; break;
        case 'End': next = n - 1; break;
        default: return;
      }
      e.preventDefault();
      if (parts[next].toggle) parts[next].toggle.focus();
    };

    /* Computers always show one open card */
    const onLayout = () => {
      if (!phone.matches && open < 0) setOpen(Math.min(last, parts.length - 1));
      parts.forEach(({ toggle }, i) => {
        if (!toggle) return;
        if (i === open && !phone.matches) toggle.setAttribute('aria-disabled', 'true');
        else toggle.removeAttribute('aria-disabled');
      });
    };

    const io = new IntersectionObserver((entries) => {
      inView = entries[entries.length - 1].isIntersecting;
      syncVideos();
    }, { rootMargin: '200px 0px' });
    io.observe(root);

    /* Theme editor: selecting a card block opens it */
    const onBlockSelect = (e) => {
      if (!root.contains(e.target)) return;
      const i = cards.findIndex((c) => c === e.target || c.contains(e.target));
      if (i >= 0) setOpen(i);
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKey);
    phone.addEventListener('change', onLayout);
    reduce.addEventListener('change', syncVideos);
    document.addEventListener('shopify:block:select', onBlockSelect);

    root._vhCards = () => {
      clearTimeout(settle);
      html.classList.remove('vh-snap-hold');
      io.disconnect();
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKey);
      phone.removeEventListener('change', onLayout);
      reduce.removeEventListener('change', syncVideos);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      parts.forEach(({ video }) => { if (video && !video.paused) video.pause(); });
      delete root._vhCards;
    };
  };

  const teardown = (root) => { if (root._vhCards) root._vhCards(); };

  if (window.VH && typeof window.VH.section === 'function') {
    window.VH.section(SELECTOR, init, teardown);
  } else {
    document.querySelectorAll(SELECTOR).forEach(init);
  }
})();
